import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AppointmentTimeline = ({ appointments, onJoinConsultation, onViewPatient }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'bg-primary text-primary-foreground';
      case 'ready':
        return 'bg-success text-success-foreground';
      case 'waiting':
        return 'bg-warning text-warning-foreground';
      case 'completed':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  const getConsultationTypeIcon = (type) => {
    switch (type) {
      case 'routine':
        return 'Calendar';
      case 'follow-up':
        return 'RotateCcw';
      case 'urgent':
        return 'AlertCircle';
      case 'consultation':
        return 'Video';
      default:
        return 'Clock';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Today's Appointments</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">
            {appointments?.filter(apt => apt?.status !== 'completed')?.length} pending
          </span>
          <Button variant="outline" size="sm" iconName="Calendar" iconPosition="left">
            View Calendar
          </Button>
        </div>
      </div>
      <div className="space-y-4">
        {appointments?.map((appointment, index) => (
          <div key={appointment?.id} className="relative">
            {/* Timeline connector */}
            {index < appointments?.length - 1 && (
              <div className="absolute left-6 top-12 w-0.5 h-16 bg-border" />
            )}
            
            <div className="flex items-start space-x-4">
              {/* Timeline dot */}
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStatusColor(appointment?.status)}`}>
                <Icon name={getConsultationTypeIcon(appointment?.type)} size={20} />
              </div>

              {/* Appointment content */}
              <div className="flex-1 bg-muted/30 rounded-lg p-4 border border-border">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-medium text-foreground">{appointment?.patientName}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        appointment?.priority === 'high' ? 'bg-error/10 text-error' :
                        appointment?.priority === 'medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                      }`}>
                        {appointment?.priority} priority
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={14} />
                        <span>{appointment?.time}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="User" size={14} />
                        <span>{appointment?.type}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPin" size={14} />
                        <span>{appointment?.location}</span>
                      </div>
                    </div>

                    {appointment?.notes && (
                      <p className="text-sm text-muted-foreground mb-3">{appointment?.notes}</p>
                    )}

                    {appointment?.patientPreparation && (
                      <div className="flex items-center space-x-2 mb-3">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        <span className="text-sm text-success">Patient preparation completed</span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center space-x-2 ml-4">
                    {appointment?.status === 'ready' && (
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Video"
                        iconPosition="left"
                        onClick={() => onJoinConsultation(appointment?.id)}
                      >
                        Join Call
                      </Button>
                    )}
                    
                    {appointment?.status === 'waiting' && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Clock"
                        iconPosition="left"
                      >
                        Patient Waiting
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="FileText"
                      onClick={() => onViewPatient(appointment?.patientId)}
                    >
                      Records
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {appointments?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No appointments today</h3>
          <p className="text-muted-foreground">Your schedule is clear for today.</p>
        </div>
      )}
    </div>
  );
};

export default AppointmentTimeline;